import React, { useCallback, useEffect, useRef, useState } from "react";
import { Paper } from "@material-ui/core";
import { useAlert } from "react-alert";
import { Modal, Spinner, Table } from "react-bootstrap";
import { ErrorMessage, Field, Formik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../_metronic/_helpers";
import { baseUrl } from "../../../services/config";
import { PremissionChecker } from "../../../services/PremissionChecker";
import CRMConversionSuccessModal from "./CRMConversionSuccessModal";
import CRMActivities from "../CRMActivities/CRMActivities";
import CRMAdmissionTestAttempts from "../CRMAdmissionTestAttempts/CRMAdmissionTestAttempts";
import { DatePicker } from "antd";
import ReactToPrint from "react-to-print";
import { DownloadTableExcel } from "react-export-table-to-excel";
import { set, upperCase } from "lodash";
import Pagination from "@material-ui/lab/Pagination";
import usePagination from "../../../components/usePagination/usePagination";

const { RangePicker } = DatePicker;
const frozenInquiryColumns = [
  { left: 0, width: 80 },
  { left: 80, width: 140 },
  { left: 220, width: 260 },
  { left: 480, width: 150 },
];

const getFrozenInquiryColumnStyle = (columnIndex, isHeader = false) => {
  const column = frozenInquiryColumns[columnIndex];

  return {
    position: "sticky",
    left: column.left,
    width: column.width,
    minWidth: column.width,
    maxWidth: column.width,
    backgroundColor: isHeader ? "#f3f6f9" : "#fff",
    zIndex: isHeader ? 4 : 3,
    ...(columnIndex === frozenInquiryColumns.length - 1
      ? { boxShadow: "3px 0 4px -3px rgba(0, 0, 0, 0.35)" }
      : {}),
  };
};

const getCurrentMonthYear = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const getCurrentDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getCurrentMonthDueDate = () => `${getCurrentMonthYear()}-10`;

const isInquiryConverted = (inquiry) =>
  inquiry?.is_converted === true ||
  Number(inquiry?.is_converted) === 1 ||
  Boolean(inquiry?.converted_student_id);

const emptyConversion = {
  year_id: "",
  campus_id: "",
  student_class_id: "",
  education_type: 1,
  global_section_id: "",
  father_name: "",
  gender: "",
  mobile_no: "",
  due_date: "",
  issue_date: "",
};

const emptyInquiry = {
  direct_registration: false,
  student_name: "",
  father_name: "",
  mobile_no: "",
  whatsapp_no: "",
  email: "",
  age: "",
  gender: "",
  date_of_birth: "",
  session_id: "",
  class_id: "",
  campus_id: "",
  section_id: "",
  education_type: 1,
  previous_school: "",
  address: "",
  city: "",
  area: "",
  source_id: "",
  status_id: 1,
  priority: "",
  assigned_to: "",
  expected_admission_date: "",
  last_activity_date: "",
  is_converted: false,
  converted_student_id: "",
  converted_at: "",
  remarks: "",
  lost_remarks: "",
  admission_id: "",
  registration_id: "",
};

const numericFields = [
  "session_id",
  "class_id",
  "campus_id",
  "section_id",
  "education_type",
  "source_id",
  "status_id",
  "assigned_to",
  "converted_student_id",
];

// const detailFields = [
//   ["Inquiry No", "inquiry_no"],
//   ["Student Name", "student_name"],
//   ["Father Name", "father_name"],
//   ["Mobile No", "mobile_no"],
//   ["WhatsApp No", "whatsapp_no"],
//   ["Email", "email"],
//   ["Age", "age"],
//   ["Gender", "gender"],
//   ["Date of Birth", "date_of_birth"],
//   ["Session ID", "session_id"],
//   ["Class ID", "class_id"],
//   ["Campus ID", "campus_id"],
//   ["Section ID", "section_id"],
//   ["Education Type", "education_type"],
//   ["Previous School", "previous_school"],
//   ["Address", "address"],
//   ["City", "city"],
//   ["Area", "area"],
//   ["Source ID", "source_id"],
//   ["Status ID", "status_id"],
//   ["Priority", "priority"],
//   ["Assigned To", "assigned_to"],
//   ["Expected Admission", "expected_admission_date"],
//   ["Last Activity", "last_activity_date"],
//   ["Converted", "is_converted"],
//   ["Converted Student ID", "converted_student_id"],
//   ["Converted At", "converted_at"],
//   ["Remarks", "remarks"],
//   ["Lost Remarks", "lost_remarks"],
//   ["Created By", "created_by"],
//   ["Updated By", "updated_by"],
//   ["Created At", "created_at"],
//   ["Updated At", "updated_at"],
// ];

const nullableId = Yup.number()
  .transform((value, originalValue) =>
    originalValue === "" || originalValue === null ? null : value,
  )
  .nullable()
  .integer("Must be a valid integer")
  .positive("Must be a valid ID");

const dateField = Yup.string()
  .nullable()
  .matches(/^$|^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD format");

const inquiryValidationSchema = Yup.object().shape({
  student_name: Yup.string()
    .trim()
    .required("Student Name is required")
    .max(125, "Maximum 125 characters allowed"),
  father_name: Yup.string()
    .trim()
    .required("Father Name is required")
    .max(125, "Maximum 125 characters allowed"),
  mobile_no: Yup.string()
    .trim()
    .required("Mobile No is required")
    .max(125, "Maximum 125 characters allowed"),
  whatsapp_no: Yup.string()
    .nullable()
    .max(125, "Maximum 125 characters allowed"),
  email: Yup.string()
    .nullable()
    .email("Enter a valid email address")
    .max(125, "Maximum 125 characters allowed"),
  age: Yup.string()
    .nullable()
    .max(125, "Maximum 125 characters allowed"),
  gender: Yup.string()
    .nullable()
    .max(125, "Maximum 125 characters allowed"),
  date_of_birth: dateField,
  session_id: nullableId,
  class_id: nullableId,
  campus_id: nullableId,
  section_id: nullableId,
  education_type: nullableId,
  previous_school: Yup.string()
    .nullable()
    .max(125, "Maximum 125 characters allowed"),
  city: Yup.string()
    .nullable()
    .max(125, "Maximum 125 characters allowed"),
  area: Yup.string()
    .nullable()
    .max(125, "Maximum 125 characters allowed"),
  source_id: nullableId,
  status_id: nullableId,
  priority: Yup.string()
    .nullable()
    .oneOf(["", "Low", "Medium", "High"]),
  assigned_to: nullableId,
  expected_admission_date: dateField,
  last_activity_date: dateField,
  converted_student_id: nullableId,
  converted_at: dateField,
  is_converted: Yup.boolean().nullable(),
  direct_registration: Yup.boolean(),
  admission_id: nullableId,
  registration_id: nullableId,
});

function InquiryField({ label, name, type = "text", required = false }) {
  return (
    <div className="col-md-4 mb-4">
      <label className="form-control-label">
        <b>
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
        </b>
      </label>
      <Field
        type={type}
        name={name}
        placeholder={`Enter ${label}`}
        className="form-control"
        maxLength={type === "text" || type === "email" ? 125 : undefined}
      />
      <ErrorMessage name={name} component="div" className="text-danger mt-1" />
    </div>
  );
}

function CRMInquiries() {
  const alert = useAlert();
  const user = useSelector((state) => state.user.user);
  const authtoken = localStorage.getItem("userToken");
  const [allInquiries, setAllInquiries] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [conversionForm, setConversionForm] = useState(emptyConversion);
  const [feesLoading, setFeesLoading] = useState(false);
  const [forceDirectRegistration, setForceDirectRegistration] = useState(false);
  const [printModel, setPrintModel] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(0);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [printData, setPrintData] = useState(false);
  const [unitName, setUnitName] = useState("");
  const [logo, setLogo] = useState("");
  const todayDate = () => {
    let d = new Date();
    var curr_moth = ("0" + (d.getMonth() + 1)).slice(-2);
    var curr_date = ("0" + d.getDate()).slice(-2);
    let newdate = `${d.getFullYear()}-${curr_moth}-${curr_date}`;
    return newdate;
  };

  const [from, setFrom] = useState("");
  const [toDate, setToDate] = useState("");
  const [inquiryStatus, setInquiryStatus] = useState("");
  const [inquirySource, setInquirySource] = useState("");
  // const [isConverted, setIsConverted] = useState(false);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const filtered = allInquiries.filter((inquiry) => {
      const inquiryDate = inquiry.created_at.split(" ")[0];

      if (from && inquiryDate < from) return false;
      if (toDate && inquiryDate > toDate) return false;

      if (inquiryStatus !== "" && inquiry.status_id !== Number(inquiryStatus))
        return false;

      if (inquirySource !== "" && inquiry.source_id !== Number(inquirySource))
        return false;

      // if (isConverted && !inquiry.is_converted) return false;

      // Search filter
      if (searchText.trim()) {
        const search = searchText.trim().toLowerCase();

        // Last part of inquiry_no (e.g. "Inq-jad-cen-08" -> "08")
        const inquiryLastPart =
          inquiry.inquiry_no
            ?.split("-")
            .pop()
            ?.toLowerCase() || "";

        const matches =
          inquiry.student_name?.toLowerCase().includes(search) ||
          inquiry.father_name?.toLowerCase().includes(search) ||
          inquiry.mobile_no?.toLowerCase().includes(search) ||
          inquiry.inquiry_no?.toLowerCase().includes(search) ||
          inquiryLastPart.includes(search);

        if (!matches) return false;
      }

      return true;
    });

    setInquiries(filtered);
  }, [
    allInquiries,
    from,
    toDate,
    inquiryStatus,
    inquirySource,
    // isConverted,
    searchText,
  ]);

  const pageStyle = `
    @page {
      size: A4 landscape !important;
      margin: 8mm !important;
    }

    @media print {
      .branch-inquiry-print {
        visibility: visible !important;
        position: static !important;
      }

      .branch-inquiry-print-only {
        left: auto !important;
        top: auto !important;
        width: 100% !important;
      }
    }
  `;
  const componentRef = useRef(null);
  const tableRef = useRef(null);
  const onBeforeGetContentResolve = React.useRef(null);
  const [printloading, setprintloading] = React.useState(false);
  const [text, setText] = React.useState("old boring text");
  console.log(text);

  const showErrors = useCallback(
    (data, fallback) => {
      const message = data?.metadata?.message;
      if (message && typeof message === "object") {
        Object.values(message).forEach((item) => alert.error(item));
      } else {
        alert.error(message || fallback);
      }
    },
    [alert],
  );

  let [page, setPage] = useState(1);
  const PER_PAGE = 10;

  const count = Math.ceil(inquiries.length / PER_PAGE);
  const _DATA = usePagination(inquiries, PER_PAGE);

  const handleChange = (e, p) => {
    setPage(p);
    _DATA.jump(p);
  };

  const getInquiries = useCallback(
    async (status = true) => {
      setLoading(status);
      try {
        const response = await fetch(`${baseUrl}/crm-inquiries`, {
          headers: { Authorization: `Bearer ${authtoken}` },
        });
        const data = await response.json();
        if (!response.ok || data?.metadata?.success === false) {
          showErrors(data, "Unable to load CRM inquiries");
          return;
        }
        const inquiries = (data?.payload || []).map((item, index) => ({
          ...item,
          SrNo: index + 1,
        }));

        setAllInquiries(inquiries);
        setInquiries(inquiries);
      } catch (error) {
        alert.error("Unable to load CRM inquiries");
      } finally {
        setLoading(false);
      }
    },
    [alert, authtoken, showErrors],
  );

  const getOptions = useCallback(async () => {
    const headers = { Authorization: `Bearer ${authtoken}` };
    try {
      const [
        sourceResponse,
        statusResponse,
        sessionResponse,
      ] = await Promise.all([
        fetch(`${baseUrl}/crm-inquiry-sources`, { headers }),
        fetch(`${baseUrl}/crm-inquiry-statuses`, { headers }),
        fetch(`${baseUrl}/session`, { headers }),
      ]);
      const [sourceData, statusData, sessionData] = await Promise.all([
        sourceResponse.json(),
        statusResponse.json(),
        sessionResponse.json(),
      ]);
      if (sourceResponse.ok && sourceData?.metadata?.success !== false) {
        setSources(sourceData?.payload || []);
      }
      if (statusResponse.ok && statusData?.metadata?.success !== false) {
        setStatuses(statusData?.payload || []);
      }
      if (sessionResponse.ok && sessionData?.metadata?.success !== false) {
        setSessions(sessionData?.payload || []);
      }
    } catch (error) {
      // The form remains usable with numeric IDs if lookup data is unavailable.
    }
  }, [authtoken]);

  const getSettingData = useCallback(async () => {
    await fetch(`${baseUrl}/get-settings`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authtoken}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setUnitName(upperCase(data.payload.settings.unit_name));
        setLogo(data.payload.settings.logo_file);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [authtoken]);

  useEffect(() => {
    getInquiries(true);
    getOptions();
    getSettingData();
  }, [getInquiries, getOptions, getSettingData]);

  const closeModal = () => {
    setModal(null);
    setSelectedInquiry(null);
    setClasses([]);
    setSections([]);
    setConversionForm(emptyConversion);
    setForceDirectRegistration(false);
  };

  const openAddModal = () => {
    const defaultCampusId = user?.campuses?.[0]?.id || "";
    getClasses(defaultCampusId);
    setModal("add");
  };

  const getClasses = async (campusId) => {
    setClasses([]);
    if (!campusId) return;
    try {
      const response = await fetch(`${baseUrl}/campusclass/${campusId}`, {
        headers: { Authorization: `Bearer ${authtoken}` },
      });
      const data = await response.json();
      if (response.ok && data?.metadata?.success !== false) {
        setClasses(data?.payload || []);
      }
    } catch (error) {
      alert.error("Unable to load classes");
    }
  };

  const getSections = async (campusId, educationType, classId) => {
    setSections([]);
    if (!campusId || !educationType || !classId) return;
    try {
      const response = await fetch(
        `${baseUrl}/class_section/${campusId}/${educationType}/${classId}`,
        { headers: { Authorization: `Bearer ${authtoken}` } },
      );
      const data = await response.json();
      if (response.ok && data?.metadata?.success !== false) {
        setSections(data?.payload || []);
      }
    } catch (error) {
      alert.error("Unable to load sections");
    }
  };

  const getAdmissionFeeOnStart = async (values) => {
    if (
      !values.campus_id ||
      !values.student_class_id ||
      !values.education_type ||
      !values.year_id
    ) {
      return [];
    }

    setFeesLoading(true);
    try {
      const response = await fetch(`${baseUrl}/admissionFeesOnStart`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${authtoken}`,
        },
        body: JSON.stringify({
          campus_id: Number(values.campus_id),
          student_class_id: Number(values.student_class_id),
          education_type_id: Number(values.education_type),
          year_id: Number(values.year_id),
          concession_id: "",
          hostel_id: null,
          vehicle_id: null,
          is_direct_admission: false,
        }),
      });
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(data, "Unable to load admission fees");
        return [];
      }
      const fees = data?.payload || [];
      const amounts = fees.map((fee) => ({
        fee_type_id: fee.fee_type_id,
        amount: Number(fee.net_amount),
        amount_after_concession: Number(fee.amount),
      }));
      return amounts;
    } catch (error) {
      alert.error("Unable to load admission fees");
      return [];
    } finally {
      setFeesLoading(false);
    }
  };

  const openConvertModal = (inquiry) => {
    setSelectedInquiry(inquiry);
    setForceDirectRegistration(true);
    getClasses(inquiry.campus_id || user?.campuses?.[0]?.id || "");
    getSections(
      inquiry.campus_id || user?.campuses?.[0]?.id || "",
      inquiry.education_type || inquiry.education_type_id || 1,
      inquiry.class_id || "",
    );
    setModal("edit");
  };

  const updateConversionField = (event) => {
    const { name, value } = event.target;
    const values = { ...conversionForm, [name]: value };
    if (name === "student_class_id" || name === "education_type") {
      values.global_section_id = "";
      getSections(
        values.campus_id,
        values.education_type,
        values.student_class_id,
      );
    }
    setConversionForm(values);
    if (["year_id", "student_class_id", "education_type"].includes(name)) {
      getAdmissionFeeOnStart(values);
    }
  };

  const searchStudent = async (registrationId) => {
    try {
      const response = await fetch(
        `${baseUrl}/student_chllan_by_admission_id`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authtoken}`,
          },
          body: JSON.stringify({
            admission_id: "",
            registration_id: registrationId,
          }),
        },
      );
      const data = await response.json();
      if (response.ok && data?.metadata?.success) {
        const studentPayload =
          data?.payload?.students || data?.payload?.student || [];
        const students = Array.isArray(studentPayload)
          ? studentPayload
          : [studentPayload];
        setSelectedStudent(students);
        setSelectedStudentId(students[0]?.id || 0);
        setPrintModel(true);
        return true;
      } else {
        showErrors(data, "Unable to load student print details");
        return false;
      }
    } catch (error) {
      alert.error("Unable to load student print details");
      return false;
    }
  };

  const submitStudentConversion = async (inquiryId, values, feeAmounts) => {
    const response = await fetch(
      `${baseUrl}/crm-inquiries/${inquiryId}/convert-to-student`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${authtoken}`,
        },
        body: JSON.stringify({
          year_id: Number(values.year_id),
          campus_id: Number(values.campus_id),
          student_class_id: Number(values.student_class_id),
          education_type: Number(values.education_type),
          father_name: values.father_name.trim(),
          gender: values.gender,
          mobile_no: values.mobile_no.trim(),
          date_of_birth: values.date_of_birth,
          email: values.email.trim(),
          address: values.address.trim(),
          fee_months: [`${getCurrentMonthYear()}-01`],
          fee_amounts: feeAmounts,
          due_date: values.due_date,
          issue_date: values.issue_date,
        }),
      },
    );
    const data = await response.json();
    if (!response.ok || data?.metadata?.success === false) {
      showErrors(data, "Unable to convert inquiry to student");
      return false;
    }

    const convertedStudent = data?.payload?.student || data?.student;
    const registrationId =
      data?.payload?.registration_id ||
      convertedStudent?.registration_id ||
      data?.payload?.inquiry?.converted_student_id;

    alert.success(
      `Inquiry successfully converted to student and registration id is ${registrationId}`,
      { timeout: 0 },
    );
    if (registrationId) {
      const studentLoaded = await searchStudent(registrationId);
      if (!studentLoaded && convertedStudent) {
        setSelectedStudent([convertedStudent]);
        setSelectedStudentId(convertedStudent.id || 0);
        setPrintModel(true);
      }
    } else if (convertedStudent) {
      setSelectedStudent([convertedStudent]);
      setSelectedStudentId(convertedStudent.id || 0);
      setPrintModel(true);
    }
    closeModal();
    getInquiries(true);
    return true;
  };

  // const convertInquiryToStudent = async (inquiryValues) => {
  //   const values = {
  //     year_id: inquiryValues.session_id,
  //     campus_id: inquiryValues.campus_id,
  //     student_class_id: inquiryValues.class_id,
  //     education_type: inquiryValues.education_type,
  //     father_name: inquiryValues.father_name,
  //     gender: inquiryValues.gender,
  //     mobile_no: inquiryValues.mobile_no,
  //     date_of_birth: inquiryValues.date_of_birth,
  //     email: inquiryValues.email,
  //     address: inquiryValues.address,
  //     due_date: getCurrentMonthDueDate(),
  //     issue_date: getCurrentDate(),
  //   };
  //   const requiredFields = [
  //     "year_id",
  //     "campus_id",
  //     "student_class_id",
  //     "education_type",
  //     "father_name",
  //     "gender",
  //     "mobile_no",
  //     "date_of_birth",
  //     "address",
  //     "due_date",
  //     "issue_date",
  //   ];
  //   if (requiredFields.some((field) => !values[field])) {
  //     alert.error("Please complete all required conversion fields");
  //     return;
  //   }
  //   const feeAmounts = await getAdmissionFeeOnStart(values);
  //   if (!feeAmounts.length) {
  //     alert.error("No admission fees are available for this selection");
  //     return;
  //   }

  //   setSubmitting(true);
  //   try {
  //     await submitStudentConversion(selectedInquiry.id, values, feeAmounts);
  //   } catch (error) {
  //     alert.error("Unable to convert inquiry to student");
  //   } finally {
  //     setSubmitting(false);
  //   }
  // };

  const openEditModal = (inquiry) => {
    setSelectedInquiry(inquiry);
    getClasses(inquiry.campus_id);
    setModal("edit");
  };

  const getInitialValues = () =>
    Object.keys(emptyInquiry).reduce(
      (values, key) => ({
        ...values,
        [key]:
          modal === "edit" && selectedInquiry
            ? key === "direct_registration"
              ? isInquiryConverted(selectedInquiry)
              : key === "session_id"
              ? selectedInquiry.session_id ||
                sessions.find(
                  (session) => String(session.active_academic_year) === "1",
                )?.id ||
                ""
              : key === "education_type"
              ? selectedInquiry.education_type ||
                selectedInquiry.education_type_id ||
                1
              : key === "is_converted"
              ? isInquiryConverted(selectedInquiry)
              : key === "converted_student_id"
              ? selectedInquiry.converted_student_id ||
                selectedInquiry.converted_student?.id ||
                ""
              : key === "converted_at" && selectedInquiry[key]
              ? selectedInquiry[key].slice(0, 10)
              : selectedInquiry[key] ?? ""
            : key === "session_id"
            ? sessions.find(
                (session) => String(session.active_academic_year) === "1",
              )?.id || ""
            : key === "campus_id"
            ? user?.campuses?.[0]?.id || ""
            : emptyInquiry[key],
      }),
      {},
    );

  const buildPayload = (values) =>
    Object.entries(values).reduce((payload, [key, value]) => {
      if (key === "direct_registration") return payload;
      if (key === "is_converted") {
        payload[key] = Boolean(values.direct_registration);
      } else if (numericFields.includes(key)) {
        payload[key] = value === "" ? null : Number(value);
      } else {
        payload[key] = value === "" ? null : value;
      }

      console.log(values);
      console.log(payload);
      return payload;
    }, {});

  const saveInquiry = async (values) => {
    const isEditing = modal === "edit";
    let directFees = [];
    const directConversionValues = {
      year_id: values.session_id,
      campus_id: values.campus_id,
      student_class_id: values.class_id,
      education_type: values.education_type,
      father_name: values.father_name,
      gender: values.gender,
      mobile_no: values.mobile_no,
      date_of_birth: values.date_of_birth,
      email: values.email,
      address: values.address,
      admission_id: values.admission_id,
      registration_id: values.registration_id,
      is_converted: values.direct_registration,
      due_date: getCurrentMonthDueDate(),
      issue_date: getCurrentDate(),
    };

    // return console.log(directConversionValues);

    if (!isEditing && values.direct_registration) {
      const requiredFields = [
        "year_id",
        "campus_id",
        "student_class_id",
        "education_type",
        "father_name",
        "mobile_no",
        // "gender",
        // "date_of_birth",
        // "email",
        // "address",
      ];
      if (requiredFields.some((field) => !directConversionValues[field])) {
        alert.error("Please complete all required direct registration fields");
        return;
      }
      directFees = await getAdmissionFeeOnStart(directConversionValues);
      if (!directFees.length) {
        alert.error("No admission fees are available for this selection");
        return;
      }
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        `${baseUrl}/crm-inquiries${isEditing ? `/${selectedInquiry.id}` : ""}`,
        {
          method: isEditing ? "PUT" : "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${authtoken}`,
          },
          body: JSON.stringify(buildPayload(values)),
        },
      );
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(data, `Unable to ${isEditing ? "update" : "add"} inquiry`);
        return;
      }
      // if (!isEditing && values.direct_registration) {
      //   const inquiryId = data?.payload?.id || data?.payload?.inquiry_id;
      //   if (!inquiryId) {
      //     alert.error("Inquiry was created, but its ID was not returned");
      //     getInquiries(true);
      //     return;
      //   }
      //   await submitStudentConversion(
      //     inquiryId,
      //     directConversionValues,
      //     directFees,
      //   );
      //   return;
      // }
      alert.success(`Inquiry successfully ${isEditing ? "updated" : "added"}`);
      closeModal();
      getInquiries(true);
    } catch (error) {
      alert.error(`Unable to ${isEditing ? "update" : "add"} inquiry`);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteInquiry = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(
        `${baseUrl}/crm-inquiries/${selectedInquiry.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${authtoken}` },
        },
      );
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(data, "Unable to delete inquiry");
        return;
      }
      alert.success(data?.payload || "Inquiry successfully deleted");
      closeModal();
      getInquiries(true);
    } catch (error) {
      alert.error("Unable to delete inquiry");
    } finally {
      setSubmitting(false);
    }
  };

  const datePicker = (value, dateString) => {
    console.log("Formatted Selected Time: ", dateString);
    if (dateString[0] !== "") setFrom(dateString[0]);
    else setFrom("");

    if (dateString[1] !== "") setToDate(dateString[1]);
    else setToDate("");
  };

  //for print
  const handleBeforePrint = React.useCallback(() => {
    console.log("`onBeforePrint` called");
  }, []);

  const handleOnBeforeGetContent = React.useCallback(() => {
    console.log("`onBeforeGetContent` called");
    setprintloading(true);
    setText("Loading new text...");

    return new Promise((resolve) => {
      onBeforeGetContentResolve.current = resolve;

      setTimeout(() => {
        setprintloading(false);
        setText("New, Updated Text!");
        resolve();
      }, 2000);
    });
  }, [setprintloading, setText]);

  const handleAfterPrint = React.useCallback(() => {
    console.log("`onAfterPrint` called");
  }, []);

  return (
    <Paper style={{ marginTop: "10px", marginBottom: "10px", padding: "15px" }}>
      <button className="btn btn-primary" onClick={openAddModal}>
        Add CRM Inquiry
      </button>

      <div className="row mt-5">
        <div className="col-md-3">
          <label className="form-control-label">
            <b>Select Date</b>{" "}
          </label>
          <RangePicker style={{ height: 40 }} onChange={datePicker} />
        </div>

        <div className="col-md-3 mb-4">
          <label className="form-control-label">
            <b>Inquiry Status</b>
          </label>
          <select
            className="form-control form-select"
            name="inquiryStatus"
            value={inquiryStatus}
            onChange={(event) => setInquiryStatus(event.target.value)}
          >
            <option value="">Select Inquiry Status</option>
            {statuses.map((status) => (
              <option key={status.id} value={status.id}>
                {status.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-3 mb-4">
          <label className="form-control-label">
            <b>Inquiry Source</b>
          </label>
          <select
            className="form-control form-select"
            name="inquirySource"
            value={inquirySource}
            onChange={(event) => setInquirySource(event.target.value)}
          >
            <option value="">Select Inquiry Source</option>
            {sources.map((status) => (
              <option key={status.id} value={status.id}>
                {status.name}
              </option>
            ))}
          </select>
        </div>

        {/* <div className="col-md-3 mt-11">
          <label className="checkbox checkbox-single">
            <input
              type="checkbox"
              className="form-checkbox"
              checked={isConverted}
              onChange={(e) => setIsConverted(e.target.checked)}
            />
            <span />
            &nbsp;&nbsp;&nbsp;Is Converted
          </label>
        </div> */}
      </div>

      <div className="row mt-5">
        <div className="col-md-6">
          <input
            className="form-control"
            placeholder="Search by Student Name, Father Name, Inquiry No or Mobile No."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        {inquiries.length > 0 && (
          <div className="col-md-4">
            <ReactToPrint
              documentTitle={`CRM Inquiries Report`}
              pageStyle={pageStyle}
              onAfterPrint={handleAfterPrint}
              onBeforeGetContent={handleOnBeforeGetContent}
              onBeforePrint={handleBeforePrint}
              removeAfterPrint={true}
              trigger={() => (
                <button
                  type="button"
                  className="btn btn-primary w-100px mx-2"
                  disabled={printloading || !componentRef.current}
                >
                  {printloading ? "Printing..." : "Print"}
                </button>
              )}
              content={() => componentRef.current}
            />
            <DownloadTableExcel
              filename="CRM Inquiries Report"
              sheet="CRM Inquiries Report"
              currentTableRef={tableRef.current}
            >
              <button type="button" className="btn btn-primary">
                Export
              </button>
            </DownloadTableExcel>
          </div>
        )}
      </div>

      <div className="table-responsive" style={{ maxHeight: "500px" }}>
        <Table
          className="mt-5"
          style={{ minWidth: "150%", overflow: "scroll" }}
        >
          <thead>
            <tr
              className="text-center"
              style={{
                position: "sticky",
                top: "0",
                zIndex: "5",
                backgroundColor: "#f3f6f9",
              }}
            >
              <th style={getFrozenInquiryColumnStyle(0, true)}>Sr. No</th>
              <th style={getFrozenInquiryColumnStyle(1, true)}>Inquiry No</th>
              <th style={getFrozenInquiryColumnStyle(2, true)}>Student</th>
              <th style={getFrozenInquiryColumnStyle(3, true)}>Mobile</th>
              <th>Source</th>
              <th>Status</th>
              {/* <th>Converted</th> */}
              <th>Last Activity</th>
              <th>Last Assessment</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {_DATA.currentData().map((inquiry, index) => (
              <tr
                className="text-center"
                key={index + 1}
                style={{
                  color:
                    Number(inquiry?.source?.id) === 1 &&
                    Number(inquiry?.status?.id) === 1
                      ? "darkblue"
                      : [1, 5, 16, 17].includes(Number(inquiry?.status?.id))
                      ? "red"
                      : "green",
                }}
              >
                <td style={getFrozenInquiryColumnStyle(0)}>{inquiry?.SrNo}</td>
                <td style={getFrozenInquiryColumnStyle(1)}>
                  {inquiry?.inquiry_no}
                </td>
                <td style={getFrozenInquiryColumnStyle(2)}>
                  {inquiry?.student_name} ({inquiry?.father_name})
                </td>
                <td style={getFrozenInquiryColumnStyle(3)}>
                  {inquiry?.mobile_no}
                </td>
                <td>{inquiry?.source?.name}</td>
                <td>{inquiry?.status?.name}</td>
                {/* <td>
                  {inquiry?.is_converted?.admission_id
                    ? inquiry?.converted_student?.admission_id
                    : inquiry?.converted_student?.registration_id || "---"}
                </td> */}
                <td>
                  {inquiry?.activities.length > 0
                    ? inquiry?.activities[inquiry?.activities.length - 1]
                        ?.activity_type?.name +
                      " (" +
                      inquiry?.activities[inquiry?.activities.length - 1]
                        ?.activity_date +
                      ")"
                    : "---"}
                </td>
                <td>
                  {inquiry?.admission_test_attempts.length > 0
                    ? inquiry?.admission_test_attempts[
                        inquiry?.admission_test_attempts.length - 1
                      ]?.test_date +
                      " (" +
                      inquiry?.admission_test_attempts[
                        inquiry?.admission_test_attempts.length - 1
                      ]?.result_status_detail?.name +
                      ")"
                    : "---"}
                </td>
                <td>{inquiry?.created_at.split(" ")[0]}</td>
                <td className="text-nowrap">
                  <button
                    type="button"
                    className="btn btn-icon btn-light btn-hover-primary-custom btn-sm mx-1"
                    title="Activity"
                    onClick={() => {
                      setSelectedInquiry(inquiry);
                      setModal("activity");
                    }}
                  >
                    <span className="svg-icon svg-icon-lg svg-icon-primary-custom">
                      <SVG
                        src={toAbsoluteUrl(
                          "/media/svg/icons/General/Attachment1.svg",
                        )}
                      />
                    </span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-icon btn-light btn-hover-primary-custom btn-sm mx-1"
                    title="Assessment"
                    onClick={() => {
                      setSelectedInquiry(inquiry);
                      setModal("assessment");
                    }}
                  >
                    <span className="svg-icon svg-icon-lg svg-icon-primary-custom">
                      <SVG
                        src={toAbsoluteUrl("/media/svg/icons/General/Save.svg")}
                      />
                    </span>
                  </button>
                  {/* {!isInquiryConverted(inquiry) &&
                    PremissionChecker(user, "crm_inquiry.update") && (
                      <button
                        type="button"
                        className="btn btn-icon btn-light btn-hover-primary-custom btn btn-sm mx-1"
                        title="Convert to Student"
                        onClick={() => openConvertModal(inquiry)}
                      >
                        <span className="svg-icon svg-icon-md svg-icon-primary-custom">
                          <SVG
                            src={toAbsoluteUrl(
                              "/media/svg/icons/Navigation/Plus.svg",
                            )}
                          />
                        </span>
                      </button>
                    )} */}
                  {PremissionChecker(user, "crm_inquiry.update") && (
                    <button
                      type="button"
                      className="btn btn-icon btn-light btn-hover-primary-custom btn-sm mx-1"
                      title="Update"
                      onClick={() => openEditModal(inquiry)}
                    >
                      <span className="svg-icon svg-icon-md svg-icon-primary-custom">
                        <SVG
                          src={toAbsoluteUrl(
                            "/media/svg/icons/Communication/Write.svg",
                          )}
                        />
                      </span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-icon btn-light btn-hover-primary-custom btn-sm mx-1"
                    title="Print"
                    onClick={() => {
                      setSelectedInquiry(inquiry);
                      setPrintModel(true);
                      setPrintData(true);
                    }}
                  >
                    <span className="svg-icon svg-icon-md svg-icon-primary-custom">
                      <SVG
                        src={toAbsoluteUrl(
                          "/media/svg/icons/Devices/Printer.svg",
                        )}
                      />
                    </span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-icon btn-light btn-hover-danger btn-sm mx-1"
                    title="Delete"
                    onClick={() => {
                      setSelectedInquiry(inquiry);
                      setModal("delete");
                    }}
                  >
                    <span className="svg-icon svg-icon-md svg-icon-danger">
                      <SVG
                        src={toAbsoluteUrl(
                          "/media/svg/icons/Communication/Delete-user.svg",
                        )}
                      />
                    </span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {loading && (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      )}
      {!loading && inquiries.length === 0 && (
        <div className="text-center py-5">No inquiries found.</div>
      )}

      <Pagination
        color="text-primary"
        count={count}
        size="large"
        page={page}
        variant="text"
        shape="round"
        onChange={handleChange}
      />

      <Modal
        show={modal === "add" || modal === "edit"}
        onHide={closeModal}
        size="xl"
      >
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            {modal === "edit" ? "Update" : "Add New"} CRM Inquiry
          </Modal.Title>
        </Modal.Header>
        <Formik
          enableReinitialize
          initialValues={getInitialValues()}
          validationSchema={inquiryValidationSchema}
          onSubmit={saveInquiry}
        >
          {({ handleSubmit, values, setFieldValue }) => (
            <form onSubmit={handleSubmit} noValidate>
              <Modal.Body>
                <div className="row mb-4">
                  <div className="switch">
                    <label
                      style={{
                        display: "flex",
                        flexDirection: "row-reverse",
                        justifyContent: "flex-end",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ paddingTop: "10px" }}>
                        Register Inquiry
                      </span>
                      <Field
                        name="direct_registration"
                        type="checkbox"
                        className="mr-2"
                        checked={values.direct_registration}
                        onChange={(event) => {
                          const enabled = event.target.checked;
                          setFieldValue("direct_registration", enabled);
                          if (enabled && values.class_id) {
                            getSections(
                              values.campus_id,
                              values.education_type,
                              values.class_id,
                            );
                          }
                          if (!enabled) {
                            setFieldValue("date_of_birth", "");
                            setFieldValue("email", "");
                            setFieldValue("admission_id", "");
                            setFieldValue("registration_id", "");
                            setFieldValue("gender", "");
                            setFieldValue("address", "");
                            setFieldValue("previous_school", "");
                          }
                        }}
                      />
                      <span class="lever"></span>
                    </label>
                  </div>
                </div>

                <Field type="hidden" name="session_id" />
                <Field type="hidden" name="campus_id" />
                <Field type="hidden" name="education_type" />
                <div className="row mb-4">
                  <InquiryField
                    label="Student Name"
                    name="student_name"
                    required
                  />
                  <InquiryField
                    label="Father Name"
                    name="father_name"
                    required
                  />

                  <InquiryField label="Mobile No" name="mobile_no" required />

                  <div className="col-md-4 mb-4">
                    <label className="form-control-label">
                      <b>Select Class</b>
                    </label>
                    <Field
                      as="select"
                      className="form-select form-control"
                      name="class_id"
                      onChange={(event) => {
                        const classId = event.target.value;
                        setFieldValue("class_id", classId);
                        setFieldValue("section_id", "");
                        if (values.direct_registration) {
                          getSections(
                            values.campus_id,
                            values.education_type,
                            classId,
                          );
                        }
                      }}
                    >
                      <option value="">Select Class</option>
                      {classes.map((studentClass) => (
                        <option key={studentClass.id} value={studentClass.id}>
                          {studentClass.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="class_id"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </div>

                  <div className="col-md-4 mb-4">
                    <label className="form-control-label">
                      <b>Inquiry Source</b>
                    </label>
                    {sources.length ? (
                      <Field
                        as="select"
                        className="form-select form-control"
                        name="source_id"
                      >
                        <option value="">Select Inquiry Source</option>
                        {sources.map((source) => (
                          <option key={source.id} value={source.id}>
                            {source.name}
                          </option>
                        ))}
                      </Field>
                    ) : (
                      <Field
                        type="number"
                        className="form-control"
                        name="source_id"
                      />
                    )}
                    <ErrorMessage
                      name="source_id"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </div>

                  <div className="col-md-4 mb-4">
                    <label className="form-control-label">
                      <b>Inquiry Status</b>
                    </label>
                    {statuses.length ? (
                      <Field
                        as="select"
                        className="form-select form-control"
                        name="status_id"
                        disabled={modal !== "edit"}
                      >
                        <option value="">Select Inquiry Status</option>
                        {statuses.map((status) => (
                          <option key={status.id} value={status.id}>
                            {status.name}
                          </option>
                        ))}
                      </Field>
                    ) : (
                      <Field
                        type="number"
                        className="form-control"
                        name="status_id"
                      />
                    )}
                    <ErrorMessage
                      name="status_id"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </div>

                  {values.direct_registration && (
                    <>
                      <InquiryField label="Admission ID" name="admission_id" />

                      <InquiryField
                        label="Registration ID"
                        name="registration_id"
                      />

                      <div className="col-md-4 mb-4">
                        <label className="form-control-label">
                          <b>Gender</b>
                        </label>
                        <Field
                          as="select"
                          className="form-select form-control"
                          name="gender"
                          required
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </Field>
                        <ErrorMessage
                          name="gender"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </div>

                      <InquiryField
                        label="Date of Birth"
                        name="date_of_birth"
                        type="date"
                      />

                      <InquiryField label="Email" name="email" type="email" />

                      <InquiryField
                        label="Previous School"
                        name="previous_school"
                      />

                      <div className="col-md-8 mb-4">
                        <label className="form-control-label">
                          <b>Address</b>
                        </label>
                        <Field
                          as="textarea"
                          className="form-control"
                          name="address"
                          label="Address"
                          placeholder="Enter Address"
                        />
                      </div>
                    </>
                  )}
                </div>
              </Modal.Body>
              <Modal.Footer>
                {/* {submitting ? (
                  <Spinner animation="border" />
                ) : modal === "edit" &&
                  values.direct_registration &&
                  !isInquiryConverted(selectedInquiry) ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => convertInquiryToStudent(values)}
                    disabled={feesLoading}
                  >
                    Convert to Student
                  </button>
                ) : ( */}

                {submitting ? (
                  <Spinner animation="border" />
                ) : (
                  <button type="submit" className="btn btn-primary w-100px">
                    {modal === "edit" ? "Update" : "Add"}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={closeModal}
                >
                  Cancel
                </button>
              </Modal.Footer>
            </form>
          )}
        </Formik>
      </Modal>

      <Modal show={modal === "activity"} onHide={closeModal} size="xl">
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            CRM Activity Details - {selectedInquiry?.student_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CRMActivities
            inquiry_id={selectedInquiry?.id}
            getInquiries={(status) => getInquiries(status)}
            closeCRMModal={closeModal}
          />
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-danger" onClick={closeModal}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={modal === "assessment"} onHide={closeModal} size="xl">
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            CRM Assessment Details - {selectedInquiry?.student_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CRMAdmissionTestAttempts
            inquiry_id={selectedInquiry?.id}
            getInquiries={(status) => getInquiries(status)}
            closeCRMModal={closeModal}
            attemptNo={
              selectedInquiry?.admission_test_attempts[
                selectedInquiry?.admission_test_attempts.length - 1
              ]?.attempt_no
            }
          />
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-danger" onClick={closeModal}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* <Modal show={modal === "convert"} onHide={closeModal} size="xl">
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            Convert Inquiry to Student
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-4 mb-4">
              <label className="form-control-label">
                <b>
                  Session <span style={{ color: "red" }}>*</span>
                </b>
              </label>
              <select
                className="form-control"
                name="year_id"
                value={conversionForm.year_id}
                onChange={updateConversionField}
              >
                <option value="">Select Session</option>
                {sessions.map((session) => (
                  <option key={session.id} value={session.id}>
                    {session.name || session.session_name || session.id}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4 mb-4">
              <label className="form-control-label">
                <b>Campus</b>
              </label>
              <input
                className="form-control"
                value={
                  user?.campuses?.find(
                    (campus) =>
                      String(campus.id) === String(conversionForm.campus_id),
                  )?.name || conversionForm.campus_id
                }
                disabled
              />
            </div>

            <div className="col-md-4 mb-4">
              <label className="form-control-label">
                <b>
                  Class <span style={{ color: "red" }}>*</span>
                </b>
              </label>
              <select
                className="form-control"
                name="student_class_id"
                value={conversionForm.student_class_id}
                onChange={updateConversionField}
              >
                <option value="">Select Class</option>
                {classes.map((studentClass) => (
                  <option key={studentClass.id} value={studentClass.id}>
                    {studentClass.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4 mb-4">
              <label className="form-control-label">
                <b>
                  Education Type <span style={{ color: "red" }}>*</span>
                </b>
              </label>
              <select
                className="form-control"
                name="education_type"
                value={conversionForm.education_type}
                onChange={updateConversionField}
              >
                <option value={1}>Regular</option>
                <option value={2}>Hifz</option>
              </select>
            </div>

            <div className="col-md-4 mb-4">
              <label className="form-control-label">
                <b>
                  Section <span style={{ color: "red" }}>*</span>
                </b>
              </label>
              <select
                className="form-control"
                name="global_section_id"
                value={conversionForm.global_section_id}
                onChange={updateConversionField}
              >
                <option value="">Select Section</option>
                {sections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4 mb-4">
              <label className="form-control-label">
                <b>
                  Gender <span style={{ color: "red" }}>*</span>
                </b>
              </label>
              <select
                className="form-control"
                name="gender"
                value={conversionForm.gender}
                onChange={updateConversionField}
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="col-md-6 mb-4">
              <label className="form-control-label">
                <b>
                  Father Name <span style={{ color: "red" }}>*</span>
                </b>
              </label>
              <input
                className="form-control"
                name="father_name"
                value={conversionForm.father_name}
                onChange={updateConversionField}
              />
            </div>
            <div className="col-md-6 mb-4">
              <label className="form-control-label">
                <b>
                  Mobile No <span style={{ color: "red" }}>*</span>
                </b>
              </label>
              <input
                className="form-control"
                name="mobile_no"
                value={conversionForm.mobile_no}
                onChange={updateConversionField}
              />
            </div>
            <div className="col-md-4 mb-4">
              <label className="form-control-label">
                <b>Fee Month</b>
              </label>
              <input
                className="form-control"
                value={getCurrentMonthYear()}
                disabled
              />
            </div>
            <div className="col-md-4 mb-4">
              <label className="form-control-label">
                <b>
                  Issue Date <span style={{ color: "red" }}>*</span>
                </b>
              </label>
              <input
                type="date"
                className="form-control"
                name="issue_date"
                value={conversionForm.issue_date}
                onChange={updateConversionField}
              />
            </div>
            <div className="col-md-4 mb-4">
              <label className="form-control-label">
                <b>
                  Due Date <span style={{ color: "red" }}>*</span>
                </b>
              </label>
              <input
                type="date"
                className="form-control"
                name="due_date"
                value={conversionForm.due_date}
                onChange={updateConversionField}
              />
            </div>
          </div>

          {feesLoading && (
            <div className="text-center py-3">
              <Spinner animation="border" />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {submitting ? (
            <Spinner animation="border" />
          ) : (
            <button
              className="btn btn-success"
              onClick={convertInquiryToStudent}
              disabled={feesLoading}
            >
              Convert to Student
            </button>
          )}
          <button className="btn btn-danger" onClick={closeModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal> */}

      <Modal show={modal === "delete"} onHide={closeModal}>
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            Are you sure you want to delete this CRM inquiry?
          </Modal.Title>
        </Modal.Header>
        <Modal.Footer>
          {submitting ? (
            <Spinner animation="border" />
          ) : (
            <button className="btn button_blue" onClick={deleteInquiry}>
              Delete
            </button>
          )}
          <button className="btn btn-danger" onClick={closeModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>

      {printModel && (
        <CRMConversionSuccessModal
          show={printModel}
          close={() => {
            setPrintModel(false);
            setPrintData(false);
          }}
          selectedStudent={selectedInquiry}
          registrationId={
            selectedStudent[0]?.registration_id || selectedStudentId
          }
          printData={printData}
          unitName={unitName}
          icon={logo}
        />
      )}
      {/* For Print */}
      <div
        ref={componentRef}
        id="printSection"
        className="branch-inquiry-print branch-inquiry-print-only p-3"
        style={{
          position: "fixed",
          left: "-10000px",
          top: 0,
          width: 1200,
          visibility: "hidden",
        }}
      >
        <div id="tableboot">
          <div className="p-3" style={{ display: "flex" }}>
            <img style={{ height: 100, width: 110 }} src={logo} alt="logo" />

            <div className="col-md-6 ml-4" style={{ paddingTop: "10px" }}>
              <h1>{unitName}</h1>
              <h3>CRM Inquiries Report</h3>
              {/* {month ? ` for ${getMonthName(month)}` : ""}
                {sessionName ? ` (Session ${sessionName})` : ""} */}

              <h4>Printing Date :- {todayDate()}</h4>
              {/* <div className="d-flex justify-content-between">
                <h5>
                  Branch :-{" "}
                  {branchId === ""
                    ? "All Branches"
                    : branches.find((b) => String(b.id) === String(branchId))
                        ?.branches}
                </h5>
                <h5>Printing Date :- {todayDate()}</h5>
              </div> */}
            </div>
          </div>

          <table
            className="table mb-0"
            style={{ color: "#111", width: "100%" }}
          >
            <thead>
              <tr className="text-center">
                <th
                  style={{
                    padding: "1px",
                    whiteSpace: "normal",
                    fontSize: "14px",
                  }}
                >
                  Sr.NO.
                </th>
                <th
                  style={{
                    padding: "1px",
                    whiteSpace: "normal",
                    fontSize: "14px",
                  }}
                >
                  Inquiry No
                </th>
                <th
                  style={{
                    padding: "1px",
                    whiteSpace: "normal",
                    fontSize: "14px",
                  }}
                >
                  Student
                </th>
                <th
                  style={{
                    padding: "1px",
                    whiteSpace: "normal",
                    fontSize: "14px",
                  }}
                >
                  Mobile
                </th>
                <th
                  style={{
                    padding: "1px",
                    whiteSpace: "normal",
                    fontSize: "14px",
                  }}
                >
                  Source
                </th>
                <th
                  style={{
                    padding: "1px",
                    whiteSpace: "normal",
                    fontSize: "14px",
                  }}
                >
                  Status
                </th>
                {/* style={{
                      padding: "1px",
                      whiteSpace: "normal",
                      fontSize: "14px",
                    }} <th>Converted</th> */}
                <th
                  style={{
                    padding: "1px",
                    whiteSpace: "normal",
                    fontSize: "14px",
                  }}
                >
                  Last Activity
                </th>
                <th
                  style={{
                    padding: "1px",
                    whiteSpace: "normal",
                    fontSize: "14px",
                  }}
                >
                  Last Assessment
                </th>
                <th
                  style={{
                    padding: "1px",
                    whiteSpace: "normal",
                    fontSize: "14px",
                  }}
                >
                  Created At
                </th>
              </tr>
            </thead>
            <tbody>
              {inquiries.map((inquiry) => (
                <tr className="text-center" key={inquiry.id}>
                  <td
                    style={{
                      padding: "1px",
                      whiteSpace: "normal",
                      fontSize: "12px",
                      border: "1px solid black",
                    }}
                  >
                    {inquiry?.SrNo}
                  </td>
                  <td
                    style={{
                      padding: "1px",
                      whiteSpace: "normal",
                      fontSize: "12px",
                      border: "1px solid black",
                    }}
                  >
                    {inquiry?.inquiry_no}
                  </td>
                  <td
                    style={{
                      padding: "1px",
                      whiteSpace: "normal",
                      fontSize: "12px",
                      border: "1px solid black",
                    }}
                  >
                    {inquiry?.student_name} ({inquiry?.father_name})
                  </td>
                  <td
                    style={{
                      padding: "1px",
                      whiteSpace: "normal",
                      fontSize: "12px",
                      border: "1px solid black",
                    }}
                  >
                    {inquiry?.mobile_no}
                  </td>
                  <td
                    style={{
                      padding: "1px",
                      whiteSpace: "normal",
                      fontSize: "12px",
                      border: "1px solid black",
                    }}
                  >
                    {inquiry?.source?.name}
                  </td>
                  <td
                    style={{
                      padding: "1px",
                      whiteSpace: "normal",
                      fontSize: "12px",
                      border: "1px solid black",
                    }}
                  >
                    {inquiry?.status?.name}
                  </td>
                  {/* <td>
                    {inquiry?.is_converted
                      ? `${inquiry?.converted_student?.id}`
                      : "---"}
                  </td> */}
                  <td
                    style={{
                      padding: "1px",
                      whiteSpace: "normal",
                      fontSize: "12px",
                      border: "1px solid black",
                    }}
                  >
                    {inquiry?.activities.length > 0
                      ? inquiry?.activities[inquiry?.activities.length - 1]
                          ?.activity_type?.name +
                        " (" +
                        inquiry?.activities[inquiry?.activities.length - 1]
                          ?.activity_date +
                        ")"
                      : "---"}
                  </td>
                  <td
                    style={{
                      padding: "1px",
                      whiteSpace: "normal",
                      fontSize: "12px",
                      border: "1px solid black",
                    }}
                  >
                    {inquiry?.admission_test_attempts.length > 0
                      ? inquiry?.admission_test_attempts[
                          inquiry?.admission_test_attempts.length - 1
                        ]?.test_date +
                        " (" +
                        inquiry?.admission_test_attempts[
                          inquiry?.admission_test_attempts.length - 1
                        ]?.result_status_detail?.name +
                        ")"
                      : "---"}
                  </td>
                  <td
                    style={{
                      padding: "1px",
                      whiteSpace: "normal",
                      fontSize: "12px",
                      border: "1px solid black",
                    }}
                  >
                    {inquiry?.created_at.split(" ")[0]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* For Export */}
      <div ref={tableRef} style={{ display: "none" }}>
        <div id="tableboot">
          <table
            className="table mb-0"
            style={{ color: "#111", width: "100%" }}
          >
            <thead>
              <tr className="text-center">
                <th>Inquiry No</th>
                <th>Student</th>
                <th>Mobile</th>
                <th>Source</th>
                <th>Status</th>
                {/* <th>Converted</th> */}
                <th>Last Activity</th>
                <th>Last Assessment</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {inquiries.map((inquiry) => (
                <tr className="text-center" key={inquiry.id}>
                  <td>{inquiry?.inquiry_no}</td>
                  <td>
                    {inquiry?.student_name} ({inquiry?.father_name})
                  </td>
                  <td>{inquiry?.mobile_no}</td>
                  <td>{inquiry?.source?.name}</td>
                  <td>{inquiry?.status?.name}</td>
                  {/* <td>
                    {inquiry?.is_converted
                      ? `${inquiry?.converted_student?.id}`
                      : "---"}
                  </td> */}
                  <td>
                    {inquiry?.activities.length > 0
                      ? inquiry?.activities[inquiry?.activities.length - 1]
                          ?.activity_type?.name +
                        " (" +
                        inquiry?.activities[inquiry?.activities.length - 1]
                          ?.activity_date +
                        ")"
                      : "---"}
                  </td>
                  <td>
                    {inquiry?.admission_test_attempts.length > 0
                      ? inquiry?.admission_test_attempts[
                          inquiry?.admission_test_attempts.length - 1
                        ]?.test_date +
                        " (" +
                        inquiry?.admission_test_attempts[
                          inquiry?.admission_test_attempts.length - 1
                        ]?.result_status_detail?.name +
                        ")"
                      : "---"}
                  </td>
                  <td>{inquiry?.created_at.split(" ")[0]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Paper>
  );
}

export default CRMInquiries;
